/**
 * ShweTap's own icons, drawn as paths.
 *
 * Emoji were placeholders and they cost more than they look. Three reasons to stop:
 *
 * - **They are not ours.** 🪙 renders as a different coin on iOS, Android, Windows
 *   and Samsung, so the game's own currency has no fixed appearance — and the one
 *   thing a currency must have is a fixed appearance.
 * - **They ignore the palette.** Every other pixel obeys the torch-lit warm ramp in
 *   `tokens.css`; emoji arrive in someone else's colours and cannot be tinted, which
 *   is why the nav had to grayscale the inactive ones to stop them shouting.
 * - **Some are not glyphs at all.** ⛏️ and ⚙️ carry variation selectors, 🧑‍🔧 is a
 *   ZWJ sequence, and platforms that lack the composed form fall back to showing the
 *   PARTS — a person and a wrench, side by side, where one tool should be.
 *
 * Design rules, so additions stay in family:
 *
 * - One 24×24 grid, `stroke-width: 2`, round caps and joins. Chunky enough to read at
 *   the 18px the nav uses.
 * - `currentColor` only. The caller owns the colour, so an icon lights up with its
 *   active tab and dims with its inactive one — no grayscale hack.
 * - Cave tools, not office metaphors: a real pickaxe head, a hurricane lantern, a
 *   backpack with a flap. The player is a miner.
 * - Silhouette first. At 18px interior detail turns to mud, so each icon is legible
 *   from its outline alone and the interior lines only add richness above ~28px.
 *
 * Accessibility is unchanged from the emoji contract in CLAUDE.md: the svg is
 * `aria-hidden` and the caller supplies the label. `title` is accepted for the rare
 * standalone case where the icon IS the control.
 */
import { Children, isValidElement, type CSSProperties, type ReactNode } from 'react';

export type IconName =
  // nav
  | 'home' | 'pick' | 'bag' | 'hero' | 'shop' | 'book'
  // currency + stats
  | 'coin' | 'kyat' | 'xp' | 'energy' | 'key'
  // the cave itself
  | 'rock' | 'sparkle' | 'flame' | 'flag'
  // gear
  | 'axe' | 'lamp' | 'torch'
  // actions and chrome
  | 'pause' | 'close' | 'check' | 'settings' | 'functions' | 'ad' | 'sound'
  | 'chevron-right' | 'chevron-up' | 'plus' | 'withdraw' | 'phone' | 'trophy'
  | 'target' | 'lock' | 'warning' | 'leaderboard' | 'companion'
  // hazards
  | 'bat' | 'gas' | 'ice' | 'wave'
  // status
  | 'hourglass' | 'void'
  // reward hub
  | 'gift' | 'auto'
  // character auras
  | 'moon' | 'radar'
  | 'play' | 'send' | 'share' | 'link' | 'cave'
  // the map to another cave
  | 'map' | 'mapPiece';

/* Each entry is the inside of a 24x24 <svg>. Kept as data rather than components so
   the set can be iterated in tests — a missing name is then a test failure, not a
   blank square in the nav. */
const PATHS: Record<IconName, JSX.Element> = {
  /* A pitched roof over a doorway. Reads as "home" at 18px where a detailed house
     collapses into a blob. */
  home: (
    <>
      <path d="M3 11.2 12 4l9 7.2" />
      <path d="M5.4 10v9.2h13.2V10" />
      <path d="M10 19.2v-5.1h4v5.1" />
    </>
  ),
  /* A pickaxe: curved head, offset haft. The game's verb. */
  pick: (
    <>
      {/* The app's hero mark: login lockup, the DIG tab, and the Enter-the-cave
          button. It used to be a shallow arc over a straight shaft, which at 16px
          collapsed into a capital T — a mining game whose icon does not read as a
          pickaxe. The head is a deep parabola now, the ends are barbed rather than
          tapering to nothing, and a binding sits where the shaft meets the head, so
          the three parts stay separable when the whole glyph is 16 pixels wide. */}
      <path d="M2.9 9.6C6.2 4.2 17.8 4.2 21.1 9.6" />
      <path d="M2.9 9.6 5.6 8.2M21.1 9.6l-2.7-1.4" />
      <path d="M10.2 8.4h3.6" />
      <path d="M12 8.4V21" />
    </>
  ),
  /* Backpack: rounded body, a flap and a grab loop. */
  bag: (
    <>
      <path d="M6 9.5a6 6 0 0 1 12 0v9.2a1.8 1.8 0 0 1-1.8 1.8H7.8A1.8 1.8 0 0 1 6 18.7z" />
      <path d="M9.6 7.2V5.4a2.4 2.4 0 0 1 4.8 0v1.8" />
      <path d="M8.6 13.6h6.8" />
    </>
  ),
  /* A miner in a hard hat — head, brim, shoulders. Not a ZWJ sequence. */
  hero: (
    <>
      <path d="M7.4 8.6a4.6 4.6 0 0 1 9.2 0" />
      <path d="M5.8 8.6h12.4" />
      <circle cx="12" cy="12.6" r="2.6" />
      <path d="M6.2 20.4c.7-3 3-4.6 5.8-4.6s5.1 1.6 5.8 4.6" />
    </>
  ),
  /* Shop: a market awning over a counter. */
  shop: (
    <>
      <path d="M4 9.2h16l-1.2 10.2a1.4 1.4 0 0 1-1.4 1.2H6.6a1.4 1.4 0 0 1-1.4-1.2z" />
      <path d="M4 9.2 6 4h12l2 5.2" />
      <path d="M9.4 13.2a2.6 2.6 0 0 0 5.2 0" />
    </>
  ),
  /* An open book, spine in the middle. */
  book: (
    <>
      <path d="M12 6.4C10.2 5 7.6 4.6 4.4 5v13.4c3.2-.4 5.8 0 7.6 1.4" />
      <path d="M12 6.4c1.8-1.4 4.4-1.8 7.6-1.4v13.4c-3.2-.4-5.8 0-7.6 1.4" />
      <path d="M12 6.4v13.4" />
    </>
  ),

  /* Coin: a struck disc, with an inner ring so it is not read as a full moon. */
  coin: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="4" />
    </>
  ),
  /* Kyat: a cut gem. The withdrawable currency, so it must never be confused with
     the coin — different silhouette, not a different colour. */
  kyat: (
    <>
      <path d="M12 3.6 20.2 9.4 12 20.4 3.8 9.4z" />
      <path d="M3.8 9.4h16.4M8.6 9.4 12 20.4l3.4-11" />
    </>
  ),
  /* XP: a four-point spark. */
  xp: (
    <>
      <path d="M12 3.2v6M12 15v5.8M4.6 12h5.4M14 12h5.4" />
      <path d="M7.4 7.4l2.6 2.6M14 14l2.6 2.6M16.6 7.4 14 10M10 14l-2.6 2.6" />
    </>
  ),
  /* Energy: a bolt. */
  energy: <path d="M13.4 3.2 6.6 13.4h4.2l-1 7.4 7.6-10.6h-4.4z" />,

  /* Rock: a boulder — asymmetric, flat-based, two facets.

     Deliberately NOT a cut gem. The first draft of this icon was `M6 3h12l4 6-10
     12L2 9z`, which is the kyat silhouette with different numbers, and the two sit
     four rows apart in the cave bag. A player glancing at a mineral count and
     reading it as withdrawable currency is the exact confusion `kyat` was given its
     own shape to prevent. */
  rock: (
    <>
      <path d="M3.2 18.4 5.4 11.2 10 7.2l6.4.8 4.2 5.4-1.4 5z" />
      <path d="M10 7.2 11.6 13l6.6 5.4M11.6 13 3.2 18.4" />
    </>
  ),

  /* Sparkle: a four-point star with concave sides. The straight-spoked burst it
     replaced was `xp` in all but name. */
  sparkle: (
    <path d="M12 3.2Q13 11 20.8 12 13 13 12 20.8 11 13 3.2 12 11 11 12 3.2z" />
  ),

  /* Flame: the lava soundscape and the heat hazards.

     The tip leans and the shoulder curls back. A symmetric point over a round belly
     is a *water drop* — the first draft was exactly that, and it read as one at
     every size. Asymmetry is what makes it fire. */
  flame: (
    <>
      <path d="M13 2.4c-.5 3.4-2.6 4.6-4.4 6.6-1.8 2-2.6 3.9-2.6 6a6 6 0 0 0 12 0c0-2.4-1-4-2.4-5.4-.3 1.3-.9 2.1-1.8 2.6.5-3.4-.1-6.4-.8-9.8z" />
      <path d="M12.2 13.6c1.3 1.2 2 2.2 2 3.3a2 2 0 0 1-4 0c0-1.1.7-2.1 2-3.3z" />
    </>
  ),

  /* Sound: a speaker cone and two arcs of air. */
  sound: (
    <>
      <path d="M11.2 5.2 6.4 9.2H2.6v5.6h3.8l4.8 4z" />
      <path d="M14.8 9.2a4 4 0 0 1 0 5.6M17.8 6.2a8 8 0 0 1 0 11.6" />
    </>
  ),

  /* Cave: an uneven rock archway around an empty, unlit mouth — the boot-error
     state ("the cave is quiet"), where there is no run to show yet. Unlike
     `home`'s straight roofline over a doorway, this arch is jagged rock and the
     inside is void, not a place to enter. */
  cave: (
    <>
      <path d="M3.4 20.4 4 13.6c.4-5 3.4-9.2 8-9.2s7.6 4.2 8 9.2l.6 6.8z" />
      <path d="M8.4 20.4 8 14.4a4 4 0 0 1 8 0l-.4 6" />
    </>
  ),

  /* Flag: a checkpoint banner on a planted pole. */
  flag: (
    <>
      <path d="M5 3.6c3-1.4 5.4.8 8 .8s3.6-.8 3.6-.8v8.8s-1 .8-3.6.8-5-2.2-8-.8z" />
      <path d="M5 3.2v17.2" />
    </>
  ),

  /* Axe: single bit on a haft — matches the level-3 Copper Axe silhouette. */
  axe: (
    <>
      <path d="M13.4 3.6c3.4.6 5.8 3 6.4 6.4-2.8 1-5.6.4-7.6-1.6-1.4-1.4-1.4-3.4 1.2-4.8z" />
      <path d="M11.6 9.6 4.4 20.4" />
    </>
  ),
  /* Lantern: bail, cap, caged glass, foot. */
  lamp: (
    <>
      <path d="M9.6 4.6a2.4 2.4 0 0 1 4.8 0" />
      <path d="M7.4 7.6h9.2l-1 2h-7.2z" />
      <path d="M8.2 9.6h7.6v7.8H8.2z" />
      <path d="M7.2 20.4h9.6" />
      <path d="M12 12v3.4" />
    </>
  ),
  /* Torch: bound head, flame above. */
  torch: (
    <>
      <path d="M12 3.4c1.8 2 2.6 3.6 2.6 5a2.6 2.6 0 0 1-5.2 0c0-1.4.8-3 2.6-5z" />
      <path d="M10.4 11.4h3.2l-.8 9h-1.6z" />
    </>
  ),

  pause: <path d="M9 5.4v13.2M15 5.4v13.2" />,
  close: <path d="M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4" />,
  check: <path d="M4.6 12.6 9.4 17.4 19.4 6.6" />,
  /* Settings: a gear with visible teeth, not a smooth ring. */
  settings: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.6M12 18.6v2.6M2.8 12h2.6M18.6 12h2.6" />
      <path d="M5.5 5.5 7.3 7.3M16.7 16.7l1.8 1.8M18.5 5.5 16.7 7.3M7.3 16.7l-1.8 1.8" />
    </>
  ),
  /* Functions: a spanner. The hub for everything that is not the cave. */
  functions: (
    <>
      <path d="M16.4 3.6a4.4 4.4 0 0 0-3.2 7.4L4.4 19.8l1.8 1.8 8.8-8.8a4.4 4.4 0 0 0 5.6-5.6l-2.6 2.6-2.6-2.6z" />
    </>
  ),
  /* Ad: a screen with a play triangle. */
  ad: (
    <>
      <rect x="3.2" y="5.2" width="17.6" height="12" rx="1.6" />
      <path d="M8.6 20.4h6.8" />
      <path d="M10.6 8.8l4.6 2.4-4.6 2.4z" />
    </>
  ),
  'chevron-right': <path d="M9.4 5.6 16 12l-6.6 6.4" />,
  'chevron-up': <path d="M5.6 15 12 8.6l6.4 6.4" />,
  plus: <path d="M12 5.2v13.6M5.2 12h13.6" />,
  /* Withdraw: money leaving, an arrow out of a wallet. */
  withdraw: (
    <>
      <path d="M3.6 8.2h13.2v10.2a1.6 1.6 0 0 1-1.6 1.6H5.2a1.6 1.6 0 0 1-1.6-1.6z" />
      <path d="M13.4 13.2h3" />
      <path d="M15.6 4.6h4.8v4.8M20.4 4.6l-6 6" />
    </>
  ),
  phone: (
    <>
      <rect x="6.6" y="2.8" width="10.8" height="18.4" rx="2" />
      <path d="M10.6 18.4h2.8" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 4.2h8v4.4a4 4 0 0 1-8 0z" />
      <path d="M8 5.4H5.4v1.8A2.6 2.6 0 0 0 8 9.8M16 5.4h2.6v1.8A2.6 2.6 0 0 1 16 9.8" />
      <path d="M12 12.6v3.6M8.6 20.4h6.8M10.2 16.2h3.6v4.2h-3.6z" />
    </>
  ),
  /* Target: missions. */
  target: (
    <>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="3.4" />
    </>
  ),
  lock: (
    <>
      <rect x="5.4" y="10.4" width="13.2" height="10" rx="1.6" />
      <path d="M8.6 10.4V7.6a3.4 3.4 0 0 1 6.8 0v2.8" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3.6 21.4 20H2.6z" />
      <path d="M12 9.4v5M12 17.2v.2" />
    </>
  ),
  leaderboard: (
    <>
      <path d="M4 20.4h16M6 20.4v-6.4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v6.4" />
      <path d="M10 20.4V9.6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v10.8" />
      <path d="M14 20.4v-4.4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4.4" />
      <path d="M12 3.6 13 6l2.6.4-1.9 1.8.4 2.6L12 9.5l-2.1 1.3.4-2.6-1.9-1.8 2.6-.4z" />
    </>
  ),
  key: (
    <>
      <circle cx="7.6" cy="12" r="4.2" />
      <path d="M11.8 12H20.4M16.4 12v3.2M19.2 12v2.2" />
    </>
  ),
  companion: (
    <>
      <path d="M8.4 9.6a3.6 3.6 0 1 1 7.2 0c0 3.2-3.6 6.4-3.6 6.4S8.4 12.8 8.4 9.6z" />
      <circle cx="12" cy="9" r="1.4" />
      <path d="M6 18.6c1.6-1.4 3.8-2.2 6-2.2s4.4.8 6 2.2" />
    </>
  ),

  /* Bat: swept wings with a notch at each shoulder, a small body between. The
     hazard overlay's flock. */
  bat: (
    <>
      <path d="M12 7.4C10.6 4.8 7 3.6 3.4 5c.6 2.4 2.4 4 4.6 4.4-2.6 1-4.6 3.2-5.6 6 3 .4 5.6-.6 7.4-2.6.4.8.9 1.5 1.5 2 .6-.5 1.1-1.2 1.5-2 1.8 2 4.4 3 7.4 2.6-1-2.8-3-5-5.6-6 2.2-.4 4-2 4.6-4.4-3.6-1.4-7.2-.2-8.6 2.4z" />
      <circle cx="12" cy="9.6" r="1" />
    </>
  ),
  /* Gas: shallow parallel ripples — tighter and thinner than `wave`, so a fog
     bank never reads as open water. */
  gas: (
    <>
      <path d="M3.6 7.8c1.4-1.2 2.8-1.2 4.2 0s2.8 1.2 4.2 0 2.8-1.2 4.2 0 2.8 1.2 4.2 0" />
      <path d="M3.6 12.6c1.4-1.2 2.8-1.2 4.2 0s2.8 1.2 4.2 0 2.8-1.2 4.2 0 2.8 1.2 4.2 0" />
      <path d="M3.6 17.4c1.4-1.2 2.8-1.2 4.2 0s2.8 1.2 4.2 0 2.8-1.2 4.2 0 2.8 1.2 4.2 0" />
    </>
  ),
  /* Ice: a faceted crystal, not a snowflake — three diameters through one
     centre so it stays a shard, not a sparkle. */
  ice: (
    <>
      <path d="M12 2.8v18.4M4.4 7.4l15.2 9.2M19.6 7.4 4.4 16.6" />
    </>
  ),
  /* Wave: two deep swells, well below `gas`'s ripple amplitude, so open water
     and mist stay two different silhouettes. */
  wave: (
    <>
      <path d="M2.6 9.6c2.2-3 4.6-3 6.8 0s4.6 3 6.8 0 4.6-3 6.8 0" />
      <path d="M2.6 16.2c2.2-3 4.6-3 6.8 0s4.6 3 6.8 0 4.6-3 6.8 0" />
    </>
  ),

  /* Hourglass: a pending payout — sand not yet run out. */
  hourglass: (
    <>
      <path d="M6.4 3.6h11.2M6.4 20.4h11.2" />
      <path d="M7.2 3.6v3.2c0 2 1.6 3.2 4.8 5.2 3.2 2 4.8 3.2 4.8 5.2v3.2M16.8 3.6v3.2c0 2-1.6 3.2-4.8 5.2-3.2 2-4.8 3.2-4.8 5.2v3.2" />
    </>
  ),
  /* Void: a cancelled request — circle with one slash, distinct from `close`'s
     crossed X. */
  void: (
    <>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M6.4 17.6 17.6 6.4" />
    </>
  ),

  /* Gift: a mystery-reward box with a ribbon. */
  gift: (
    <>
      <rect x="3.6" y="9.6" width="16.8" height="10.8" rx="1" />
      <path d="M3.6 13.6h16.8" />
      <path d="M12 9.6v10.8" />
      <path d="M12 9.6C9.4 9.6 7.8 8.2 7.8 6.4a2.4 2.4 0 0 1 4.2-1.6c.4.5.7 1.1 1 1.9M12 9.6c2.6 0 4.2-1.4 4.2-3.2a2.4 2.4 0 0 0-4.2-1.6c-.4.5-.7 1.1-1 1.9" />
    </>
  ),
  /* Auto: a looping arrow — hands-off, repeating. */
  auto: (
    <>
      <path d="M19.4 12a7.4 7.4 0 1 1-2.3-5.4" />
      <path d="M19.6 3.6v4.4h-4.4" />
    </>
  ),

  /* Moon: a crescent, cut by an offset circle rather than a filled shape, so it
     stays a clean outline at stroke width 2. */
  moon: <path d="M14.6 3.4a9 9 0 1 0 6 15.4c-5.6 1-10.4-3.6-9.4-9.2a9 9 0 0 1 3.4-6.2z" />,
  /* Radar: a dish on a stand, with one ping ring — the satellite-dish silhouette,
     not the concentric-circle `target`. */
  radar: (
    <>
      <path d="M4.4 13.6A8 8 0 0 1 12 6.4" />
      <path d="M4.4 13.6 12 17.6l5.6-3" />
      <path d="M12 17.6V21M9 21h6" />
      <circle cx="17.6" cy="8.4" r="1.4" />
    </>
  ),

  /* Play: resume. A closed triangle, not `chevron-right`'s open angle. */
  play: <path d="M6.4 4.4 19.2 12 6.4 19.6z" />,
  /* Send: a paper plane — Telegram link-in, not a courier's `withdraw` envelope. */
  send: <path d="M21 3.4 10.4 13.6M21 3.4 14.4 20.6l-3.6-7-7-3.6z" />,
  /* Share: the iOS Safari glyph — an arrow out of a tray, not `chevron-up`'s
     open angle or `withdraw`'s wallet. */
  share: (
    <>
      <path d="M12 15V4.4M8 8l4-4 4 4" />
      <path d="M5.6 11.6v7a1.6 1.6 0 0 0 1.6 1.6h9.6a1.6 1.6 0 0 0 1.6-1.6v-7" />
    </>
  ),
  /* Link: two interlocking chain links — the guest-account connect nag. */
  link: (
    <>
      <path d="M9.6 14.4 14.4 9.6" />
      <path d="M10.8 6.8 12.6 5a3.4 3.4 0 0 1 4.8 4.8l-1.8 1.8" />
      <path d="M13.2 17.2 11.4 19a3.4 3.4 0 0 1-4.8-4.8l1.8-1.8" />
    </>
  ),
  /* Map: a folded survey map — three panels, the outer two creased up and down, with
     a marked spot. Not `book` (a spine and two flat leaves) and not `flag` (a pole),
     because this one has to read as "a place to go" at 18px. */
  map: (
    <>
      <path d="M3.2 6.2 9 4.2v13.6l-5.8 2z" />
      <path d="M9 4.2 15 6.6v13.2L9 17.8z" />
      <path d="M15 6.6 20.8 4.6v13.6L15 19.8z" />
      <circle cx="12" cy="11" r="1.5" />
    </>
  ),
  /* One torn layer of that map. Deliberately a DIFFERENT silhouette from `map`
     rather than a smaller copy: a fragment is a thing you are carrying and a map is
     a place you can go, and confusing the two would tell the player they can enter a
     cave they have three of six layers for.

     Wide, not tall. The first version was 9.6 units across on a 24 grid and at the
     12px this is used at it read as a vertical sliver with a squiggle in it — the
     exact "interior detail turns to mud" failure the notes at the top of this file
     warn about. The tear is the whole silhouette, so it has to be the thing you see:
     three straight edges and one ragged one, at a size where the ragged one survives. */
  mapPiece: (
    <>
      <path d="M3.6 5.4h13.8l-1.8 2.8 3 2-2.8 2.2 3 2.2-2.6 1.8 1.4 2.2H3.6z" />
      <path d="M7 10.2h4" />
    </>
  ),
};

export const ICON_NAMES = Object.keys(PATHS) as IconName[];

/**
 * One icon's shapes, flattened to comparable strings.
 *
 * Exported for the tests, not for rendering. "Coin and kyat must not look alike" is
 * the kind of rule that a name-based test cannot check at all — two icons can have
 * different names and identical geometry, which is exactly how `rock` shipped as a
 * second copy of the kyat gem. Comparing the drawings as data catches it.
 */
export function iconGeometry(name: IconName): string[] {
  const shapes: string[] = [];

  const walk = (node: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;
      const props = child.props as Record<string, unknown>;

      if (typeof props.d === 'string') shapes.push(`path:${props.d}`);
      else if (child.type === 'circle') shapes.push(`circle:${props.cx},${props.cy},${props.r}`);
      else if (child.type === 'rect') {
        shapes.push(`rect:${props.x},${props.y},${props.width},${props.height}`);
      }

      /* Fragments carry the multi-shape icons; their children are the real ones. */
      if (props.children !== undefined) walk(props.children as ReactNode);
    });
  };

  walk(PATHS[name]);
  return shapes;
}

export function Icon({ name, size = 20, className = '', style, title }: {
  name: IconName;
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** Only when the icon is the whole control and has no visible label. */
  title?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title && <title>{title}</title>}
      {PATHS[name]}
    </svg>
  );
}
